import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  Box,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  IconButton,
  Divider,
  Avatar,
  InputLabel,
  FormControl,
} from "@mui/material";
// --- MUI Icons for UI Enhancement ---
import VisibilityIcon from "@mui/icons-material/Visibility";
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import LogoutButton from "layouts/logout";
import api from "core/interceptors/api";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

// Helper: Generates the full URL for a media file
const getImageUrl = (filePath) => {
  if (!filePath) return null;
  const API_BASE_URL = api.defaults.baseURL || "";
  const normalizedPath = filePath.replace(/\\/g, '/');
  return `${API_BASE_URL}/vendor_register/media/${normalizedPath}`;
};

// --- ENHANCED Helper Components ---
const DetailItem = ({ icon, label, value }) => (
  <Box display="flex" alignItems="center" mb={1.5}>
    {icon && <Box color="text.secondary" mr={1.5} sx={{ display: 'flex' }}>{icon}</Box>}
    <Box>
      <MDTypography variant="button" fontWeight="bold" color="secondary" textTransform="capitalize">
        {label}
      </MDTypography>
      <MDTypography variant="body2" fontWeight="regular" color="text">
        {value || "N/A"}
      </MDTypography>
    </Box>
  </Box>
);

const ImageItem = ({ label, filePath }) => {
  const imageUrl = getImageUrl(filePath);
  if (!imageUrl) return null;

  return (
    <Grid item xs={6} sm={4} md={3} lg={2} textAlign="center">
      <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mb={1}>
        {label}
      </MDTypography>
      <a href={imageUrl} target="_blank" rel="noreferrer">
        <Box
          component="img"
          src={imageUrl}
          alt={label}
          sx={{
            width: '100%',
            aspectRatio: '1 / 1',
            objectFit: 'cover',
            borderRadius: '12px',
            border: '1px solid #ddd',
            p: '4px',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'scale(1.05)',
              boxShadow: 3,
            }
          }}
        />
      </a>
    </Grid>
  );
};


function InactiveVendors() {
  const [selectedValue, setSelectedValue] = useState("Active");
  const [filteredData, setFilteredData] = useState([]);
  const [profileData, setProfileData] = useState(null);
  const [openProfileModal, setOpenProfileModal] = useState(false);
  const navigate = useNavigate();

  // Pagination states for DataTable
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const columnsBase = [
    { Header: "S.No.", accessor: "sno", width: "5%" }, // Changed ID to S.No.
    { Header: "Business Name", accessor: "business_name" },
    { Header: "Owner Name", accessor: "owner_name" },
    { Header: "Mobile", accessor: "mobile_number" },
    { Header: "Status", accessor: "status",
      Cell: ({ row }) => {
        const { approved_status, status } = row.original;
        let backgroundColor = "#f44336"; // Red (Default)
        let displayText = status;

        if (approved_status === "Approved") {
          backgroundColor = "#4caf50"; // Green
          displayText = "Approved";
        } else if (status === "Inactive") {
          backgroundColor = "#fb8c00"; // Orange
          displayText = "Inactive";
        } else if (approved_status === "Suspended") {
          backgroundColor = "#ffb300"; // Amber
          displayText = "Suspended";
        } else if (approved_status === "Pending") { // Handle Pending status
          backgroundColor = "#2196f3"; // Blue for pending
          displayText = "Pending";
        }

        return (
          <MDBox
            component="span"
            sx={{
              fontSize: '0.75rem',
              fontWeight: 'bold',
              padding: "6px 10px",
              borderRadius: "8px",
              color: "white",
              backgroundColor: backgroundColor
            }}
          >
            {displayText}
          </MDBox>
        );
      }
    },
    { Header: "Details", accessor: "options", align: 'center' },
    { Header: "Actions", accessor: "actions", align: 'center' },
  ];

  useEffect(() => {
    fetchVendorData(selectedValue);
  }, [selectedValue]);

  const handleDropdownChange = (event) => {
    setSelectedValue(event.target.value);
    setPage(0); // Reset page to 0 when filter changes
  };

  const fetchVendorData = async (status) => {
    try {
      const res = await api.get(`/vendor_register/fetch_by_status?status=${status}`);
      if (res.data.vendors) {
        const mapped = res.data.vendors.map((vendor, index) => ({ // Add index for S.No.
          sno: index + 1, // Calculate serial number
          ...vendor,
          options: (
            <IconButton color="info" onClick={() => handleViewProfile(vendor)} title="View Profile">
              <VisibilityIcon />
            </IconButton>
          ),
          actions: (
            <Box display="flex" gap={1} justifyContent="center">
              {vendor.status === "Inactive" && (
                <MDButton size="small" variant="gradient" color="success" onClick={() => handleStatusChange(vendor.email, "Active")}>
                  Activate
                </MDButton>
              )}
              {vendor.status === "Active" && (
                <MDButton size="small" variant="gradient" color="error" onClick={() => handleStatusChange(vendor.email, "Inactive")}>
                  Deactivate
                </MDButton>
              )}
            </Box>
          ),
        }));
        setFilteredData(mapped);
        // Adjust page if current page is out of bounds after fetching new data
        if (page * rowsPerPage >= (res.data.vendors || []).length && (res.data.vendors || []).length > 0) {
          setPage(0);
        } else if ((res.data.vendors || []).length === 0) {
          setPage(0);
        }
      } else {
        console.error("Failed to fetch vendor data:", res.data.message);
        setFilteredData([]);
      }
    } catch (error) {
      console.error("Error fetching vendor data:", error);
      setFilteredData([]);
    }
  };

  // --- UPDATED: This function now auto-switches the filter ---
  const handleStatusChange = async (email, newStatus) => {
    try {
      const res = await api.put(`/vendor_register/vendor/${email}/status`, { new_status: newStatus });
      if (res.data.status === 1) {
        await Swal.fire("Success", `Vendor status changed to ${newStatus}`, "success");
        // This will now automatically switch the filter to "Active" or "Inactive"
        // and trigger the useEffect to refetch the correct list.
        setSelectedValue(newStatus);
      } else {
        throw new Error(res.data.message);
      }
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to update status", "error");
    }
  };

  const handleViewProfile = (vendor) => {
    setProfileData(vendor);
    setOpenProfileModal(true);
  };

  // Pagination handlers
  const handleRowsPerPageChange = (event, newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when rows per page changes
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Apply pagination to filteredData
  const paginatedTableData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);


  return (
    <DashboardLayout>
      <MDBox display="flex" justifyContent="flex-end" alignItems="center">
        <LogoutButton
          onLogout={() => {
            navigate("/login");
          }}
        />
      </MDBox>
      <DashboardNavbar />

      <MDBox py={3}>
        <Card>
          {/* --- ENHANCED Page Header --- */}
          <MDBox p={3} lineHeight={1}>
            <Grid container alignItems="center">
              <Grid item xs={12} sm={8}>
                <MDTypography variant="h5" fontWeight="medium">
                  Vendor Status
                </MDTypography>
                <MDTypography variant="button" color="text">
                  Activate, deactivate, and view vendor profiles.
                </MDTypography>
              </Grid>
              <Grid item xs={12} sm={4} mt={{ xs: 2, sm: 0 }}>
                <FormControl fullWidth>
                  <InputLabel id="status-filter-label">Filter by Status</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    value={selectedValue}
                    label="Filter by Status"
                    onChange={handleDropdownChange}
                    sx={{ height: 45 }}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                    
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </MDBox>
          <MDBox p={3}>
            <DataTable
              table={{ columns: columnsBase, rows: paginatedTableData }}
              isSorted={true}
              entriesPerPage={{ defaultValue: rowsPerPage, entries: [5, 10, 15, 20, 25] }}
              showTotalEntries={true}
              canSearch={true}
              noEndBorder
              pagination={{
                variant: "contained",
                color: "dark",
                page: page,
                onPageChange: handlePageChange,
                rowsPerPage: rowsPerPage,
                onRowsPerPageChange: handleRowsPerPageChange,
                count: filteredData.length,
              }}
            />
          </MDBox>
        </Card>
      </MDBox>

      {/* --- FULLY REDESIGNED VENDOR PROFILE MODAL --- */}
      <Dialog open={openProfileModal} onClose={() => setOpenProfileModal(false)} fullWidth maxWidth="lg" scroll="body">
        <DialogContent sx={{ p: 0 }}>
          {profileData && (
            <Box>
                {/* --- Modal Header with Avatar --- */}
                <MDBox p={3} sx={{ backgroundColor: '#f8f9fa' }}>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item>
                            <Avatar
                                src={getImageUrl(profileData.logo || profileData.vendor_photo)}
                                alt={profileData.owner_name}
                                sx={{ width: 80, height: 80, border: '2px solid #fff', boxShadow: 2 }}
                            >
                               <BusinessIcon />
                            </Avatar>
                        </Grid>
                        <Grid item>
                            <MDTypography variant="h5" fontWeight="medium">{profileData.business_name}</MDTypography>
                            <MDTypography variant="body2" color="text.secondary">Owner: {profileData.owner_name}</MDTypography>
                            <MDTypography variant="caption" color="text.secondary">ID: {profileData.id}</MDTypography>
                        </Grid>
                    </Grid>
                </MDBox>
                <Divider sx={{ m: 0 }}/>

                <Box p={3}>
                    {/* --- Section: Business & Legal --- */}
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <MDTypography variant="h6" display="flex" alignItems="center" gap={1}><BusinessIcon color="info"/>Business & Legal</MDTypography>
                        <Divider sx={{ my: 1 }}/>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}><DetailItem label="Business Category" value={profileData.business_category} /></Grid>
                          <Grid item xs={12} md={4}><DetailItem label="Business Type" value={profileData.business_type} /></Grid>
                          <Grid item xs={12} md={4}><DetailItem label="Keywords" value={profileData.keywords} /></Grid>
                          <Grid item xs={12} md={4}><DetailItem label="GST Number" value={profileData.gst} /></Grid>
                          <Grid item xs={12} md={4}><DetailItem label="PAN Card" value={profileData.pan_card_number} /></Grid>
                          <Grid item xs={12} md={4}><DetailItem label="Aadhar" value={profileData.aadhar_number} /></Grid>
                        </Grid>
                    </Paper>

                    {/* --- Section: Contact & Location --- */}
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <MDTypography variant="h6" display="flex" alignItems="center" gap={1}><ContactPhoneIcon color="info"/>Contact & Location</MDTypography>
                        <Divider sx={{ my: 1 }}/>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}><DetailItem label="Email" value={profileData.email} /></Grid>
                            <Grid item xs={12} md={4}><DetailItem label="Mobile" value={profileData.mobile_number} /></Grid>
                            <Grid item xs={12} md={4}><DetailItem label="Alternative Mobile" value={profileData.alternative_number} /></Grid>
                            <Grid item xs={12}><DetailItem icon={<LocationOnIcon fontSize="small"/>} label="Location" value={profileData.current_location} /></Grid>
                        </Grid>
                    </Paper>

                    {/* --- Section: Bank & Payment --- */}
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <MDTypography variant="h6" display="flex" alignItems="center" gap={1}><AccountBalanceWalletIcon color="info"/>Bank & Payment</MDTypography>
                        <Divider sx={{ my: 1 }}/>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6} lg={3}><DetailItem label="Bank Name" value={profileData.bank_name} /></Grid>
                          <Grid item xs={12} md={6} lg={3}><DetailItem label="Branch Name" value={profileData.branch_name} /></Grid>
                          <Grid item xs={12} md={6} lg={3}><DetailItem label="Account Number" value={profileData.account_number} /></Grid>
                          <Grid item xs={12} md={6} lg={3}><DetailItem label="IFSC Code" value={profileData.ifsc_code} /></Grid>
                          <Grid item xs={12} md={6} lg={3}><DetailItem label="GPay" value={profileData.gpay} /></Grid>
                          <Grid item xs={12} md={6} lg={3}><DetailItem label="PhonePe" value={profileData.phonepe} /></Grid>
                        </Grid>
                    </Paper>

                    {/* --- Section: Documents & Images --- */}
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <MDTypography variant="h6" display="flex" alignItems="center" gap={1}><CloudUploadIcon color="info"/>Documents & Images</MDTypography>
                        <Divider sx={{ my: 1 }}/>
                        <Grid container spacing={2} justifyContent="flex-start" sx={{ mt: 1 }}>
                            <ImageItem label="Logo" filePath={profileData.logo} />
                            <ImageItem label="Vendor Photo" filePath={profileData.vendor_photo} />
                            <ImageItem label="Shop Photo" filePath={profileData.shop_photo} />
                            <ImageItem label="Signature" filePath={profileData.signature_photo} />
                            <ImageItem label="QR Code" filePath={profileData.qr_code} />
                            <ImageItem label="MSME Cert." filePath={profileData.msm_certificate} />
                            <ImageItem label="SGO GPS" filePath={profileData.sgo_gps_photo} />
                            {Array.isArray(profileData.user_proof_doc) && profileData.user_proof_doc.map((doc, index) => (
                                <ImageItem key={index} label={`Proof ${index + 1}`} filePath={doc} />
                            ))}
                        </Grid>
                    </Paper>
                </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-end", p: 2, borderTop: '1px solid #dee2e6' }}>
          <MDButton onClick={() => setOpenProfileModal(false)} variant="outlined" color="secondary">
            Close
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default InactiveVendors;